import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { apiGet } from "../lib/api.js";

interface Wedding {
  id: string; name: string; status: string; day_count: number; created_at_utc_ms: number;
}
interface Package {
  id: string; wedding_id: string; name: string; status: string;
  total_cents: number; currency: string; created_at_utc_ms: number;
  job_id: string | null; moment_count: number;
}
interface FavoriteVendor {
  vendor_id: string; name: string; category: string;
  city: string | null; region: string | null;
  primary_image_id: string | null;
  quality_score: number | null;
  saved_at_utc_ms: number;
}
interface SavedResponse {
  weddings: Wedding[];
  packages: Package[];
  favorites: { vendors: FavoriteVendor[] };
}

const API_BASE = (): string =>
  (window as unknown as { __BRIDEPATH_API__?: string }).__BRIDEPATH_API__ ?? "";

export function Saved() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><SavedInner /></SignedIn>
    </>
  );
}

function SavedInner() {
  const { getToken } = useAuth();
  const [data, setData] = useState<SavedResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<SavedResponse>("/saved", token);
      if (!cancelled && res.ok) setData(res.value);
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  if (!data) {
    return <section className="max-w-4xl mx-auto px-6 py-16 text-stone-500">Loading…</section>;
  }

  return (
    <section className="max-w-4xl mx-auto px-6 py-8">
      <h2 className="font-display text-3xl mb-6">Saved</h2>

      <section className="mb-10">
        <h3 className="text-xs uppercase tracking-wide text-stone-500 mb-3">Your previews ({data.packages.length})</h3>
        {data.packages.length === 0 ? (
          <div className="text-sm text-stone-500 italic">
            No previews yet. <Link to="/plan" className="underline">Plan your wedding</Link> to generate one.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.packages.map((p) => (
              <li key={p.id} className="border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <div className="font-display text-lg">{p.name}</div>
                    <div className="text-xs text-stone-500">
                      {p.moment_count} scene{p.moment_count === 1 ? "" : "s"} ·
                      ${(p.total_cents / 100).toFixed(2)} ·
                      <StatusPill status={p.status} />
                    </div>
                  </div>
                  {p.job_id && (
                    <Link
                      to={`/vision/${p.job_id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-stone-900 text-stone-50 hover:bg-stone-700"
                    >
                      Open →
                    </Link>
                  )}
                </div>
                <div className="text-[11px] text-stone-400 mt-1">
                  {new Date(p.created_at_utc_ms).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wide text-stone-500 mb-3">Saved vendors ({data.favorites.vendors.length})</h3>
        {data.favorites.vendors.length === 0 ? (
          <div className="text-sm text-stone-500 italic">
            No saved vendors yet. Click the ♡ on any vendor page to save it.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.favorites.vendors.map((v) => (
              <li key={v.vendor_id} className="border border-stone-200 rounded-lg overflow-hidden bg-white hover:shadow-sm transition">
                <Link to={`/vendor/${v.vendor_id}`} className="block">
                  {v.primary_image_id ? (
                    <FavoriteImage imageId={v.primary_image_id} alt={v.name} />
                  ) : (
                    <div className="aspect-[4/3] bg-stone-100 flex items-center justify-center text-stone-400 text-xs">no image</div>
                  )}
                  <div className="p-3">
                    <div className="font-medium text-stone-900">{v.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      {v.category}
                      {v.city && <span> · {v.city}</span>}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "ready" ? "bg-emerald-100 text-emerald-800" :
    status === "generating" ? "bg-amber-100 text-amber-800" :
    status === "archived" ? "bg-red-100 text-red-800" :
    "bg-stone-100 text-stone-700";
  return <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${cls}`}>{status}</span>;
}

function FavoriteImage({ imageId, alt }: { imageId: string; alt: string }) {
  const { getToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await fetch(`${API_BASE()}/admin/image/${imageId}`, { headers: { authorization: `Bearer ${token}` } });
      if (!res.ok || cancelled) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      revoke = url;
      if (!cancelled) setBlobUrl(url);
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [imageId, getToken]);
  if (!blobUrl) return <div className="aspect-[4/3] bg-stone-100" />;
  return <img src={blobUrl} alt={alt} className="w-full aspect-[4/3] object-cover" />;
}
